/**
 * <s-media-stage> — landscape product media stage with prev/next commands.
 *
 * Required markup:
 *   [data-stage-slide]   — one per media item; the first is active on load.
 *   [data-stage-root] > [data-stage-counter] — text node (in the shared stage root) updated with the current index ("01").
 *   button[commandfor=<stage id>][command="--prev" | "--next"].
 * Inactive slides get hidden + inert; swaps crossfade with WAAPI when motion is allowed.
 */
class SMediaStage extends HTMLElement {
  #ac;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', this.#onCommand, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #slides() {
    return [...this.querySelectorAll('[data-stage-slide]')];
  }

  #onCommand = (event) => {
    const slides = this.#slides;
    if (slides.length < 2) return;
    if (event.command === '--next') this.#show((this.#index + 1) % slides.length);
    if (event.command === '--prev') this.#show((this.#index - 1 + slides.length) % slides.length);
  };

  async #show(next) {
    const slides = this.#slides;
    const outgoing = slides[this.#index];
    const incoming = slides[next];
    this.#index = next;

    incoming.hidden = false;
    incoming.inert = false;
    incoming.removeAttribute('aria-hidden');

    const counter = this.closest('[data-stage-root]')?.querySelector('[data-stage-counter]');
    if (counter) counter.textContent = String(next + 1).padStart(2, '0');

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce) {
      incoming.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
      await outgoing.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }).finished.catch(() => {});
    }

    if (this.#index !== next) return;
    outgoing.hidden = true;
    outgoing.inert = true;
    outgoing.setAttribute('aria-hidden', 'true');
  }
}

if (!customElements.get('s-media-stage')) customElements.define('s-media-stage', SMediaStage);
